import React, { useState } from 'react';
import { Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './GoraiDocsPage.css';

// Documentation content
const docsContent = {
  index: {
    title: 'Documentation',
    subtitle: 'Getting started with Gorai',
    icon: '📖',
    content: `
## Documentation Coming Soon

Gorai is currently in active development. Comprehensive documentation is being prepared and will include:

### What's Coming

- **Getting Started Guide** — Installation, first robot, deployment basics
- **Framework Specification** — Complete technical specification
- **Robot Definition Language (RDL)** — Configuration format reference
- **Component Development** — Building custom sensors and actuators
- **Service Development** — Creating AI/ML services
- **Deployment Guide** — From single robot to fleet management
- **API Reference** — Complete API documentation

### In the Meantime

- Check out the [Framework Designs](/gorai/docs/designs) for architectural insights
- Visit [Strategic Info](/gorai/info) for project vision and positioning
- Follow development on [GitHub](https://github.com/emergingrobotics/gorai)

---

## Quick Start Preview

While full documentation is in progress, here's a preview of the basic workflow:

### 1. Install Gorai CLI

\`\`\`bash
curl -sfL https://get.gorai.dev | sh
\`\`\`

### 2. Initialize K3s Cluster

\`\`\`bash
gorai cluster init
gorai cluster status
\`\`\`

### 3. Create Robot Configuration

\`\`\`json
{
  "$schema": "https://gorai.dev/schemas/rdl-v3.json",
  "version": "3",
  "robot": {
    "name": "my-robot",
    "description": "Example robot with camera"
  },
  "components": [
    {
      "name": "main_camera",
      "type": "camera",
      "model": "v4l2",
      "attributes": {
        "device": "/dev/video0",
        "width": 640,
        "height": 480
      }
    }
  ],
  "dashboard": {
    "enabled": true
  }
}
\`\`\`

### 4. Deploy

\`\`\`bash
gorai deploy robot.json
gorai status my-robot
gorai dashboard my-robot
\`\`\`

---

## Hardware Requirements

| Component | Minimum | Recommended |
|-----------|---------|-------------|
| **Compute** | Raspberry Pi 4 (4GB) | Raspberry Pi 5 (8GB) |
| **Storage** | USB 3.0 SSD (128GB) | NVMe SSD (256GB) |
| **Cost** | ~$105 | ~$145 |

**Not supported:** Pi 3, Pi Zero, Pi 4 (2GB), SD card-only deployments

---

## Stay Updated

Documentation is being actively developed. For the latest updates:

- Watch the [GitHub repository](https://github.com/emergingrobotics/gorai)
- Follow [@emergingrobotics](https://twitter.com/emergingrobotics)
- Join our community discussions
`
  },
  designs: {
    title: 'Framework Designs',
    subtitle: 'Robotics framework design comparison and architectural decisions',
    icon: '⚙️',
    content: `
## Robotics Framework Design Comparison

This document summarizes and compares three major robotics frameworks: ROS 2, Viam, and YARP. Understanding their designs informs the architecture decisions for Gorai.

---

## Framework Summaries

### ROS 2 (Robot Operating System 2)

**Philosophy**: A comprehensive set of libraries and tools for building robot applications, emphasizing standardization, QoS control, and multi-vendor middleware support.

**Core Architecture**:
- **Node-centric**: Each node performs one logical function
- **DDS-based**: Built on Data Distribution Service with pluggable implementations
- **Strongly-typed**: Interface Definition Language (.msg, .srv, .action files)
- **Decentralized discovery**: No central broker required (unlike ROS 1's roscore)

**Communication Patterns**:

| Pattern | Purpose | Cardinality |
|---------|---------|-------------|
| Topics | Continuous data streams | Many-to-many |
| Services | Synchronous RPC | One server, many clients |
| Actions | Long-running tasks with feedback | One server, many clients |

**Key Technical Choices**:
- Middleware: DDS (Cyclone, Fast-DDS, Connext) or Zenoh
- Languages: C++ (rclcpp), Python (rclpy), C (rclc for micro-ROS)
- Build: CMake + ament + colcon (heavy toolchain)
- Serialization: CDR (via DDS) or custom per middleware
- QoS: Fine-grained policies (reliability, durability, history, deadline, liveliness)

**Strengths**:
- Mature ecosystem with thousands of packages
- Industry standard with broad adoption
- Comprehensive QoS for varied network conditions
- Real-time capable with proper configuration
- Simulation integration (Gazebo, RViz)

**Weaknesses**:
- Steep learning curve
- Complex build system
- Heavy resource footprint
- DDS complexity leaks through abstraction

---

### Viam

**Philosophy**: A modern, cloud-native robotics platform emphasizing simplicity, configuration-driven operation, and first-class AI/ML integration.

**Core Architecture**:
- **Resource-centric**: Everything is a resource (component or service)
- **Configuration-driven**: JSON config with hot reload
- **Go-first**: Primary implementation in Go
- **Cloud-native**: Deep integration with cloud services

**Resource Model**:

\`\`\`
API Triplet:     namespace:type:subtype     (e.g., rdk:component:motor)
Model Triplet:   namespace:family:name      (e.g., rdk:builtin:gpio)
\`\`\`

**Key Technical Choices**:
- Communication: gRPC + Protocol Buffers
- Transports: Direct gRPC, WebRTC, Unix sockets
- Languages: Go primary; SDKs in Python, TypeScript, C++, Rust
- Build: Go modules (simple)
- Plugin System: External processes communicating via gRPC

**Built-in AI/ML Services**:
- Vision (detection, classification, segmentation)
- ML Model (tensor inference)
- SLAM (localization and mapping)
- Navigation (waypoint-based, geospatial)
- Data capture (for training pipelines)

**Strengths**:
- Simple, unified resource abstraction
- Hot reconfiguration without restart
- First-class AI/ML integration
- Cloud fleet management
- Lower barrier to entry than ROS 2

**Weaknesses**:
- Smaller ecosystem than ROS 2
- Cloud dependency for full features
- No microcontroller support
- Younger project with less community

---

### YARP (Yet Another Robot Platform)

**Philosophy**: "Reluctant middleware" designed for longevity through loose coupling and transport neutrality. Minimally intrusive on system architecture.

**Core Architecture**:
- **Port-based**: Ports are the fundamental communication primitive
- **Transport-neutral**: Carriers abstract away protocols
- **Name server**: Centralized registry for port discovery
- **Device abstraction**: Clean separation of hardware interfaces

**Communication Model**:

\`\`\`
[Port A] ---[carrier]---> [Port B]
         ---[carrier]---> [Port C]

Carriers: tcp, udp, mcast, shmem, local, text
\`\`\`

**Key Technical Choices**:
- Languages: C++ primary, Python bindings
- Serialization: Bottle (self-describing) or Portable interface
- IDL: Apache Thrift for RPC definitions
- Build: CMake
- Discovery: Name server + multicast fallback

**Network Wrapper Pattern (NWS/NWC)**:

\`\`\`
[Device] <--attach--> [NWS] <--network--> [NWC] <--interface--> [Application]
\`\`\`

- NWS (Network Wrapper Server): Exposes device over network
- NWC (Network Wrapper Client): Provides same interface as local device
- Application code unchanged whether device is local or remote

**Strengths**:
- Elegant transport abstraction
- Non-intrusive design philosophy
- Excellent device interface separation
- Runtime port monitors for data processing
- ROS interoperability

**Weaknesses**:
- Smaller community than ROS
- Less active development
- Limited AI/ML integration
- Name server as single point of coordination

---

## Comparative Analysis

### Similarities

All three frameworks share fundamental design patterns despite different implementations:

#### 1. Communication Abstraction

Each framework abstracts transport details behind a clean interface:

| Framework | Abstraction Layer | Protocol Options |
|-----------|-------------------|------------------|
| ROS 2 | rmw (middleware interface) | DDS vendors, Zenoh |
| Viam | gRPC transports | Direct, WebRTC, Unix sockets |
| YARP | Carriers | tcp, udp, mcast, shmem, local |

#### 2. Publish/Subscribe Pattern

All support streaming data between components:

| Framework | Construct | Buffering |
|-----------|-----------|-----------|
| ROS 2 | Topics | QoS history policies |
| Viam | Streaming gRPC | Bidirectional streams |
| YARP | Ports (BufferedPort) | ODP or FIFO modes |

#### 3. Request/Response Pattern

All provide RPC mechanisms:

| Framework | Construct | Definition |
|-----------|-----------|------------|
| ROS 2 | Services | .srv files |
| Viam | gRPC methods | Protocol Buffers |
| YARP | RpcClient/RpcServer | Thrift IDL |

#### 4. Named Addressing

Resources identified by hierarchical names:

| Framework | Naming Scheme | Example |
|-----------|---------------|---------|
| ROS 2 | \`/namespace/name\` | \`/robot/camera/image_raw\` |
| Viam | \`namespace:type:subtype\` | \`rdk:component:camera\` |
| YARP | \`/port/path\` | \`/icub/camera/left\` |

#### 5. Discovery Mechanisms

Automatic component discovery:

| Framework | Method | Central Point |
|-----------|--------|---------------|
| ROS 2 | DDS multicast / Zenoh router | None (decentralized) or Zenoh router |
| Viam | Cloud registry + local | Cloud service or local discovery |
| YARP | Name server + multicast | Name server |

#### 6. Device/Hardware Abstraction

Interfaces separating hardware from logic:

| Framework | Abstraction | Example Interface |
|-----------|-------------|-------------------|
| ROS 2 | Hardware interfaces (ros2_control) | \`hardware_interface::SystemInterface\` |
| Viam | Component interfaces | \`motor.Motor\`, \`camera.Camera\` |
| YARP | Device driver interfaces | \`IFrameGrabberImage\`, \`IPositionControl\` |

---

### Differences

#### Primary Implementation Language

| Framework | Language | Rationale |
|-----------|----------|-----------|
| ROS 2 | C++ | Performance, real-time, existing codebase |
| Viam | Go | Simplicity, concurrency, deployment |
| YARP | C++ | Performance, robotics tradition |

**Implication for Gorai**: Go choice aligns with Viam; enables simpler builds and better concurrency patterns than C++.

#### Middleware Philosophy

| Framework | Approach | Trade-off |
|-----------|----------|-----------|
| ROS 2 | Pluggable vendor implementations | Flexibility vs. complexity |
| Viam | Single protocol (gRPC) | Simplicity vs. less flexibility |
| YARP | Transport-neutral carriers | Flexibility vs. carrier maintenance |

**Implication for Gorai**: NATS provides a middle ground—single protocol with flexible patterns (pub/sub, request/reply, JetStream for persistence).

#### Discovery Architecture

| Framework | Model | Trade-off |
|-----------|-------|-----------|
| ROS 2 | Decentralized (DDS) | No SPOF vs. multicast complexity |
| Viam | Centralized (cloud) | Simple management vs. cloud dependency |
| YARP | Centralized (name server) | Simple lookup vs. SPOF |

**Implication for Gorai**: NATS can provide both—embedded server for simple setups, clustered for reliability.

#### AI/ML Integration

| Framework | Level | Approach |
|-----------|-------|----------|
| ROS 2 | Package ecosystem | Third-party packages, perception stack |
| Viam | First-class services | Built-in Vision, ML Model, SLAM, Navigation |
| YARP | Minimal | Not a primary focus |

**Implication for Gorai**: Follow Viam's approach with AI as first-class citizen, but leverage TPU/NPU for edge inference.

---

## Design Space Matrix

| Dimension | ROS 2 | Viam | YARP | Gorai Target |
|-----------|-------|------|------|--------------|
| **Primary Language** | C++ | Go | C++ | Go + TinyGo |
| **Middleware** | DDS/Zenoh | gRPC | Custom | NATS |
| **Discovery** | Decentralized | Cloud/local | Name server | NATS embedded/cluster |
| **AI/ML** | Packages | First-class | Minimal | First-class + TPU/NPU |
| **Cloud** | Ecosystem | Core | None | Optional |
| **RT Support** | Yes | No | Limited | Via TinyGo on MCUs |
| **Build** | Heavy | Simple | Moderate | Simple |
| **MCU Support** | micro-ROS | No | No | TinyGo |
| **Complexity** | High | Moderate | Moderate | Low |

---

## Key Takeaways for Gorai

### Adopt

1. **Resource-centric model** (Viam): Unified abstraction for components and services
2. **Named addressing** (All): Hierarchical, human-readable identifiers
3. **Transport abstraction** (YARP philosophy): NATS as unified transport
4. **Configuration-driven** (Viam): JSON config with hot reload
5. **Device interfaces** (All): Clean hardware abstraction
6. **First-class AI/ML** (Viam): Built-in services, not afterthought
7. **Simple build** (Viam): Go modules, no complex toolchain
8. **NWS/NWC pattern** (YARP): Transparent local/remote resource access

### Differentiate

1. **NATS as core**: Simpler than DDS, more capable than gRPC for pub/sub
2. **TinyGo support**: Unified language from MCU to cloud
3. **TPU/NPU focus**: Edge AI as primary, not secondary
4. **No cloud dependency**: Standalone first, cloud optional
5. **Lower barrier**: Simpler than ROS 2, more flexible than Viam

### Avoid

1. Heavy build systems
2. Mandatory cloud connectivity
3. Complex middleware abstractions
4. Central coordinators as single points of failure
5. Monolithic designs that hinder modularity

---

## Conclusion

ROS 2, Viam, and YARP represent three generations and philosophies of robotics middleware:

- **ROS 2**: Comprehensive, standardized, complex—the "Linux kernel" of robotics
- **Viam**: Modern, cloud-native, AI-ready—the "Kubernetes" approach to robots
- **YARP**: Elegant, transport-neutral, longevity-focused—the "Unix philosophy" in middleware

Gorai can learn from all three:
- ROS 2's communication patterns and QoS concepts
- Viam's simplicity, configuration-driven operation, and AI integration
- YARP's transport neutrality and non-intrusive philosophy

The unique position for Gorai lies in combining:
- Go's simplicity with TinyGo's MCU reach
- NATS's unified messaging patterns
- First-class AI/ML with TPU/NPU acceleration
- Low barrier to entry without sacrificing capability
`
  }
};

// Sidebar navigation component
const DocsSidebar = ({ currentPath }) => {
  const navItems = [
    { path: '/gorai/docs', label: 'Getting Started', icon: '📖' },
    { path: '/gorai/docs/designs', label: 'Framework Designs', icon: '⚙️' },
  ];

  return (
    <aside className="gorai-docs-sidebar">
      <div className="gorai-sidebar-header">
        <Link to="/gorai" className="gorai-sidebar-logo">
          <img src="/images/gorai.png" alt="" className="gorai-sidebar-logo-img" />
          <span>Gorai Docs</span>
        </Link>
      </div>
      <nav className="gorai-sidebar-nav">
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`gorai-sidebar-link ${currentPath === item.path ? 'active' : ''}`}
          >
            <span className="gorai-sidebar-icon">{item.icon}</span>
            <span className="gorai-sidebar-label">{item.label}</span>
          </Link>
        ))}
      </nav>
      <div className="gorai-sidebar-footer">
        <a 
          href="https://github.com/emergingrobotics/gorai" 
          target="_blank" 
          rel="noopener noreferrer"
          className="gorai-sidebar-github"
        >
          View on GitHub →
        </a>
      </div>
    </aside>
  );
};

// Document content component
const DocContent = ({ docKey }) => {
  const doc = docsContent[docKey];

  if (!doc) {
    return <Navigate to="/gorai/docs" replace />;
  }

  return (
    <motion.article
      className="gorai-doc-content"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
    >
      <header className="gorai-doc-header">
        <span className="gorai-doc-icon">{doc.icon}</span>
        <h1>{doc.title}</h1>
        <p className="gorai-doc-subtitle">{doc.subtitle}</p>
      </header>

      <div className="gorai-doc-body">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>
          {doc.content}
        </ReactMarkdown>
      </div>
    </motion.article>
  );
};

const GoraiDocsPage = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="gorai-docs-page">
      <DocsSidebar currentPath={location.pathname} />
      
      <button 
        className="gorai-mobile-menu-toggle"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
      >
        {mobileMenuOpen ? '✕' : '☰'} Menu
      </button>

      {mobileMenuOpen && (
        <div className="gorai-mobile-sidebar" onClick={() => setMobileMenuOpen(false)}>
          <DocsSidebar currentPath={location.pathname} />
        </div>
      )}

      <main className="gorai-docs-main">
        <div className="gorai-docs-container">
          <Routes>
            <Route index element={<DocContent docKey="index" />} />
            <Route path="designs" element={<DocContent docKey="designs" />} />
          </Routes>
        </div>
      </main>
    </div>
  );
};

export default GoraiDocsPage;

